import React from "react";

import { breadcrumbSchema, termsPageSchema } from "../seo/structuredData";
import {
  PRODUCT,
  POLICY_LAST_UPDATED,
  PRICING_SENTENCE,
} from "../seo/siteMeta";

import ContentPage from "./ContentPage";

/**
 * Terms of service.
 *
 * The commercial clauses restate what the FAQ and pricing page already promise ($250 non-refundable
 * setup, $2.50 per Scout, PayPal fees non-refundable, funds dispersed after the event), so a
 * prospect cannot find one answer here and a different one there. Change one and change all three.
 *
 * Written in the same plain voice as the rest of the site rather than in legalese. An organizer who
 * cannot understand a clause cannot comply with it, and these readers are volunteers, not counsel.
 */
const sections = [
  {
    heading: "What these terms cover",
    body: [
      "These terms are the agreement between BadgeReg and the people who use it: the Scouting unit that engages BadgeReg to run a merit badge college, the volunteers that unit gives access to, and the parents and guardians who register Scouts. Creating an account, registering a Scout, or otherwise using the service means you accept them.",
      "Throughout, the unit that runs the event is the organizer, and BadgeReg is the software and the people operating it. Where an individual accepts these terms for a unit, that person confirms they are authorized to bind the unit.",
    ],
  },
  {
    heading: "Eligibility and accounts",
    body: [
      "Accounts are for adults. You must be at least 18 to hold one, which is why a Scout's registration is created and held by a parent, a guardian, or an organizer rather than by the Scout.",
      "Accounts belong to named people and are not to be shared. Keep your password to yourself, give us accurate information, and keep it current. Whoever holds an account is responsible for what is done with it, so tell us promptly at " +
        PRODUCT.supportEmail +
        " if you think someone else has got into yours.",
      "Organizers decide who gets administrative access to their event and are responsible for removing it when a volunteer's involvement ends.",
    ],
  },
  {
    heading: "What BadgeReg provides",
    body: [
      "BadgeReg provides hosted software for publishing a class schedule, taking Scout registrations, collecting payment for them, and administering and reporting on the event. The organizer is issued a web address for the event in the form of a subdomain such as " +
        PRODUCT.tenantDomainPattern +
        ".",
      "The organizer remains responsible for the event itself: the programme, the counselors, safety, compliance with their council's and their unit's requirements, and everything that happens in the room on the day. BadgeReg is not the event organizer and does not run merit badge colleges.",
    ],
  },
  {
    heading: "Fees",
    body: [
      PRICING_SENTENCE,
      "The setup fee is charged to the organizer rather than folded into registrations, because it is what holds a booked event accountable. The per-Scout fee is included in the registration price a family pays at checkout.",
      "We may change our fees, and if we do we will give at least 30 days' notice before the new prices take effect. An event you have already booked keeps the pricing it was quoted, so a change cannot land on you part-way through a fundraiser you have already opened for registration.",
    ],
  },
  {
    heading: "Payments, payouts and refunds",
    body: [
      "Payments are collected through BadgeReg's PayPal integration; the organizer does not need their own PayPal merchant account. Proceeds are dispersed to the organizer after the fundraiser is complete.",
      "Several things about refunds are worth stating plainly, because they are the ones that cause disputes:",
    ],
    bullets: [
      "The $250 setup fee is non-refundable, including if the event is cancelled.",
      "PayPal's processing fees are non-refundable. Where a registration is refunded, the processing fee already taken on it is deducted from the amount returned.",
      "Refunds to families are issued at the organizer's direction. BadgeReg will not reverse a family's payment without the organizer asking, other than where we are required to.",
      "Where a Scout changes classes and the price differs, BadgeReg charges or refunds the difference automatically.",
      "If a family disputes a charge with their bank or with PayPal, the organizer carries the outcome of that dispute, including any fee PayPal levies for it, because the money involved is the event's rather than ours.",
    ],
  },
  {
    heading: "Cancelling an event",
    body: [
      "If you need to cancel, tell us as early as you can so we can work out the least disruptive way to handle registrations already taken and money already collected. The setup fee does not come back, and PayPal's fees on any refunded registrations are still gone.",
    ],
  },
  {
    heading: "Acceptable use",
    body: ["You agree not to use BadgeReg to do any of the following:"],
    bullets: [
      "Attempt to reach data belonging to another event, another family, or another account.",
      "Probe, scan, or attack the service, or try to work around its access controls or rate limits. Reporting a vulnerability responsibly, as described on our security page, is explicitly welcome and is not a breach of this clause.",
      "Upload unlawful content, or content that has no business in a Scouting registration system.",
      "Use the service to collect personal information for a purpose unrelated to running the event.",
      "Send bulk unsolicited email through the event's notification features.",
      "Copy, decompile, reverse engineer, or attempt to derive the source of the software, or resell or sublicense access to it.",
    ],
  },
  {
    heading: "The public demo",
    body: [
      "The demo sandbox exists so anyone can try BadgeReg without signing up. It runs on invented sample data and is wiped and re-seeded on a schedule, which means anything typed into it is temporary and visible to whoever explores it next.",
      "Do not enter real personal information into the demo, and in particular do not enter real information about a Scout. Nothing in the demo is a real registration, no payment taken there is real, and no data there is retained for you.",
    ],
  },
  {
    heading: "Who owns the data",
    body: [
      "The event's data belongs to the organizing unit. To run the service we need permission to handle it, so the organizer grants BadgeReg the limited right to host, store, process, transmit, and display that data for the purpose of providing the service and supporting the event, as described in our privacy policy. We do not sell it and do not use it to market to your families.",
      "Export your roster, counselor list, and transaction ledger before your event closes out. That export is the unit's own record, and relying on us to hold data indefinitely is not a good plan for records your unit needs to keep.",
      "If you send us feature requests or feedback, we may use it to improve BadgeReg without owing you payment or attribution. A good share of what the product does today started as an organizer's email.",
    ],
  },
  {
    heading: "Our intellectual property",
    body: [
      "The BadgeReg software, its interface, and its name remain ours. What you receive is a limited, non-exclusive, non-transferable right to use the service for your event while these terms are in force. Nothing here transfers ownership of the software to you, just as nothing here transfers ownership of your event's data to us.",
    ],
  },
  {
    heading: "Services we depend on",
    body: [
      "Two parts of the service are delivered by someone else, and their terms apply alongside ours. PayPal processes payments under its own agreement with the payer, and we do not control its decisions, holds, or fees.",
      "Receipts and event notices are sent through an email account the organizer configures for their event. The organizer is responsible for having the right to use that mailbox and for the credentials it supplies, and mail delivery ultimately depends on that provider rather than on us.",
    ],
  },
  {
    heading: "Availability",
    body: [
      "BadgeReg is a small, volunteer-run operation and provides the service on a best-effort basis. There is no uptime commitment, no service level agreement, and no guaranteed backup or recovery window. Maintenance may cause brief interruptions, and we try to keep those away from the days registration is open.",
      "For anything time-critical (the hour registration opens, the morning of the event) have a fallback. Exported rosters printed on paper have saved more than one merit badge college.",
    ],
  },
  {
    heading: "No warranty",
    body: [
      "The service is provided as is and as available, without warranties of any kind, whether express or implied, including any implied warranty of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that the service will be uninterrupted, error free, or that it will meet a requirement we have not agreed to in writing.",
    ],
  },
  {
    heading: "Limits of liability",
    body: [
      "To the fullest extent the law allows, BadgeReg is not liable for indirect, incidental, special, consequential, or punitive damages, or for lost revenue, lost fundraising proceeds, lost data, or reputational harm.",
      "Where liability cannot be excluded, our total liability arising out of or relating to the service is limited to the fees you paid us for the event the claim relates to. Nothing here limits liability that cannot lawfully be limited, including for fraud or for death or personal injury caused by negligence.",
    ],
  },
  {
    heading: "Indemnification",
    body: [
      "The organizer agrees to cover BadgeReg against claims, losses, and reasonable legal costs arising from how the organizer ran its event: the content it published, the access it granted, its compliance with its council's and its unit's requirements, and any use of the service that breaks these terms. This does not apply to the extent a claim arises from our own failure.",
    ],
  },
  {
    heading: "Suspension and termination",
    body: [
      "You may stop using BadgeReg at any time, subject to the fee terms above. We may suspend or end access where these terms are breached, where fees go unpaid, or where continuing would put other users' data at risk, and we will tell you why rather than going quiet.",
      "When an event ends or access is terminated, you can ask us for an export of the event's data for at least 30 days afterwards. Fees already paid are not refunded on termination, and the clauses that are meant to outlast the agreement continue to apply.",
    ],
  },
  {
    heading: "Resolving a dispute",
    body: [
      "If something goes wrong, talk to us first. Email " +
        PRODUCT.supportEmail +
        " describing the problem and what you want done about it, and we will work with you in good faith for 30 days to sort it out. Most disagreements about an event are a misunderstanding about money or access and are fixed in a single conversation.",
      "If that does not resolve it, either of us can take the matter to court. There is no compulsory arbitration in these terms and no class-action waiver.",
    ],
  },
  {
    heading: "Governing law",
    body: [
      "These terms are governed by the laws of the State of California, without regard to its conflict-of-laws rules. The state and federal courts located in San Diego County, California have exclusive jurisdiction over any dispute, and both sides consent to venue there.",
      "If a mandatory consumer-protection law where you live gives you rights that this section cannot override, that law still applies to you.",
    ],
  },
  {
    heading: "No Scouting affiliation",
    body: [
      "BadgeReg is independent software. It is not run by, endorsed by, or affiliated with a Scouting council or the Boy Scouts of America. Merit badge names and Scouting marks belong to their respective owners and are used here only to describe what the software organizes.",
    ],
  },
  {
    heading: "Changes to these terms",
    body: [
      "If these terms change we will update this page and move the date at the top. Changes that affect a booked event's fees or refund position will be raised with the organizer directly rather than left to be discovered here. Continuing to use the service after a change takes effect means you accept the revised terms.",
    ],
  },
  {
    heading: "General",
    body: ["A short list of the standard provisions, in plain terms:"],
    bullets: [
      "These terms, together with the privacy policy, are the whole agreement between us about the service, and they replace anything said beforehand.",
      "If a court finds one clause unenforceable, the rest stays in force and that clause is narrowed only as far as necessary.",
      "Not enforcing a term once does not waive our right to enforce it later.",
      "You may not transfer this agreement without our consent. We may transfer it as part of a merger, acquisition, or transfer of the service, on notice to you.",
      "Neither side is liable for a failure caused by something genuinely outside its control, such as a hosting or payment provider outage, a natural disaster, or a network failure.",
      "Notices to you go to the email address on your account; notices to us go to " +
        PRODUCT.supportEmail +
        ".",
      "Nothing here makes either side the other's partner, employer, or agent.",
    ],
  },
  {
    heading: "Contact",
    body: [
      `Questions about these terms can go to ${PRODUCT.supportEmail} or through the contact form on this site.`,
    ],
  },
];

export default function Terms() {
  return (
    <ContentPage
      route="/terms"
      heading="Terms of Service"
      badge="Law.svg"
      badgeLabel="Law"
      standfirst="The agreement covering use of BadgeReg: what we provide, what the fees are, how refunds and cancellations work, who owns the data, and where our liability ends."
      effectiveDate={POLICY_LAST_UPDATED}
      sections={sections}
      jsonLd={[termsPageSchema, breadcrumbSchema("Terms", "/terms")]}
    />
  );
}
